import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { useEffect, useRef, useState } from 'react';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Heading2,
} from 'lucide-react';
import { Button, Dialog, Input } from '@/design-system';
import { cn } from '@/design-system/utils/cn';

/** Estilos explícitos — Tailwind Preflight zera headings/listas e não há plugin typography. */
const editorProseClassName = cn(
  'announcement-prose min-h-[180px] max-h-[320px] overflow-y-auto px-3 py-2 focus:outline-none',
  'max-w-none text-sm leading-relaxed text-gray-900',
  '[&_p]:my-2',
  '[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:leading-tight',
  '[&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:leading-snug',
  '[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-lg [&_h3]:font-semibold',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-0.5',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-200 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-gray-600',
  '[&_strong]:font-bold',
  '[&_em]:italic',
  '[&_u]:underline',
  '[&_s]:line-through',
  '[&_a]:text-primary [&_a]:underline',
  '[&_img]:my-2 [&_img]:max-w-full [&_img]:rounded-md',
  // Placeholder TipTap
  '[&_p.is-editor-empty:first-child::before]:pointer-events-none [&_p.is-editor-empty:first-child::before]:float-left [&_p.is-editor-empty:first-child::before]:h-0 [&_p.is-editor-empty:first-child::before]:text-gray-400 [&_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]'
);

type AnnouncementRichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  onUploadImage: (file: File) => Promise<string>;
  disabled?: boolean;
};

export function AnnouncementRichTextEditor({
  value,
  onChange,
  onUploadImage,
  disabled,
}: AnnouncementRichTextEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadingRef = useRef(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('https://');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      Image.configure({ allowBase64: false }),
      Placeholder.configure({ placeholder: 'Escreva o comunicado…' }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: editorProseClassName,
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const normalize = (html: string) => {
      const stripped = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      return stripped === '' ? '' : html;
    };
    if (normalize(value || '') !== normalize(current)) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled, false);
  }, [disabled, editor]);

  if (!editor) return null;

  const openLinkDialog = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    setLinkUrl(prev || 'https://');
    setLinkDialogOpen(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
    setLinkDialogOpen(false);
  };

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setLinkDialogOpen(false);
  };

  const handleImagePick = async (file: File | null) => {
    if (!file || uploadingRef.current) return;
    uploadingRef.current = true;
    try {
      const url = await onUploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
    } catch {
      // Parent/mutation already surfaced the error (toast); avoid unhandled rejection
    } finally {
      uploadingRef.current = false;
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const btn = (active: boolean) =>
    cn(
      'h-8 w-8 p-0',
      active ? 'bg-primary/10 text-primary' : 'text-gray-600'
    );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap gap-1 border-b border-gray-200 bg-gray-50 px-2 py-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={btn(editor.isActive('bold'))}
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          aria-label="Negrito"
        >
          <Bold size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={btn(editor.isActive('italic'))}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
          aria-label="Itálico"
        >
          <Italic size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={btn(editor.isActive('heading', { level: 2 }))}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          disabled={disabled}
          aria-label="Título"
        >
          <Heading2 size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={btn(editor.isActive('bulletList'))}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled}
          aria-label="Lista"
        >
          <List size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={btn(editor.isActive('orderedList'))}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled}
          aria-label="Lista numerada"
        >
          <ListOrdered size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={btn(editor.isActive('link'))}
          onClick={openLinkDialog}
          disabled={disabled}
          aria-label="Link"
        >
          <LinkIcon size={16} />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={btn(false)}
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          aria-label="Imagem"
        >
          <ImageIcon size={16} />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={(e) => void handleImagePick(e.target.files?.[0] ?? null)}
        />
      </div>
      <EditorContent editor={editor} />

      <Dialog
        isOpen={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
        title="Inserir link"
        size="sm"
        footer={(
          <>
            <Button type="button" variant="ghost" onClick={removeLink}>Remover</Button>
            <Button type="button" variant="ghost" onClick={() => setLinkDialogOpen(false)}>Cancelar</Button>
            <Button type="button" variant="primary" onClick={applyLink}>Aplicar</Button>
          </>
        )}
      >
        <div className="space-y-4">
          <Input
            label="URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                applyLink();
              }
            }}
          />
        </div>
      </Dialog>
    </div>
  );
}
